import React, { useEffect, useRef, useState } from "react";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { KJUUP_COLORS, buildImageUrl } from "../../helper/constants";
import { buildVideoPosterUrl, buildVideoSrc, getVideoMimeType } from "../../utils/chatVideo";
import LanguageContext from "../../helper/LanguageContext";
import { $user } from "../../helper/UserFactory";
import { getFingerprintHeaders } from "../../helper/apiUtils";
import "../../styles/components.css";
import "./ImageSection.css";
import { useDispatch } from "react-redux";
import { SET_VIEWER_IMAGES, SET_VIEWER_IINITAL_INDEX, SET_VIEWER_IS_INTERNAL } from "../../store";

const isVisualMedia = (item) =>
  item?.messageType === "image" || item?.messageType === "video";

function hexToRgba(hex, alpha) {
  let c = hex.replace("#", "");
  if (c.length === 3) {
    c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
  }
  const bigint = parseInt(c, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function EmailImageThumb({ item, apiBaseUrl, onClick, onLoaded, loaded, accent }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    const load = async () => {
      try {
        const token = $user.getToken();
        const res = await fetch(
          `${apiBaseUrl}/email/attachments/${item.attachmentId}/preview`,
          {
            headers: {
              Authorization: `jwt ${token}`,
              ...getFingerprintHeaders(),
            },
          }
        );
        if (!res.ok) throw new Error("preview failed");
        const blob = await res.blob();
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        revoke = objectUrl;
        setUrl(objectUrl);
      } catch (_) {
        if (!cancelled) setFailed(true);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [apiBaseUrl, item.attachmentId]);

  if (failed) {
    return (
      <div
        onClick={onClick}
        className="image-item image-section__fallback"
        title={item.attachedFileName}
      >
        <span className="material-symbols-outlined image-section__fallback-icon">
          image
        </span>
      </div>
    );
  }

  return (
    <>
      {!loaded && (
        <div className="image-section__loading">
          <div className="image-skeleton-loader image-section__spinner" />
        </div>
      )}
      {url && (
        <img
          onClick={onClick}
          className={`image-item image-section__img${loaded ? " image-section__img--loaded" : ""}`}
          src={url}
          alt=""
          onLoad={onLoaded}
        />
      )}
    </>
  );
}

function ThumbSpinner({ accent }) {
  return (
    <div className="image-section__loading image-section__loading--dark">
      <div className="image-skeleton-loader image-section__spinner" />
    </div>
  );
}

/**
 * First-frame fallback when a video has no JPEG poster (older messages, or
 * poster extraction failed). Never leaves a broken <img> in the grid.
 */
function VideoFrameThumb({ src, onClick, onReady }) {
  const ref = useRef(null);
  const [failed, setFailed] = useState(!src);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    const el = ref.current;
    if (!el || !src) {
      setFailed(true);
      onReadyRef.current?.();
      return undefined;
    }
    let settled = false;
    const settle = (ok) => {
      if (settled) return;
      settled = true;
      if (!ok) setFailed(true);
      onReadyRef.current?.();
    };
    const showFrame = () => {
      try {
        if (el.currentTime < 0.05) el.currentTime = 0.15;
      } catch (_) {
        /* ignore seek errors on some codecs */
      }
    };
    const onMeta = () => showFrame();
    const onReadyFrame = () => settle(true);
    const onErr = () => settle(false);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("loadeddata", onReadyFrame);
    el.addEventListener("seeked", onReadyFrame);
    el.addEventListener("error", onErr);
    if (el.readyState >= 2) onReadyFrame();
    else if (el.readyState >= 1) onMeta();
    const timeout = window.setTimeout(() => settle(el.readyState >= 2), 4000);
    return () => {
      window.clearTimeout(timeout);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("loadeddata", onReadyFrame);
      el.removeEventListener("seeked", onReadyFrame);
      el.removeEventListener("error", onErr);
    };
  }, [src]);

  if (failed) {
    return (
      <div
        onClick={onClick}
        className="image-item media-video-fallback"
      />
    );
  }

  const frameSrc = src && !src.includes("#") ? `${src}#t=0.1` : src;

  return (
    <video
      ref={ref}
      className="image-item"
      src={frameSrc}
      muted
      playsInline
      preload="metadata"
      onClick={onClick}
    />
  );
}

function MediaVideoThumb({ item, accent, loaded, onLoaded, onClick }) {
  const poster = buildVideoPosterUrl(false, item?.thumbnailUrl, item?.fileUrl);
  const videoSrc = buildVideoSrc(false, item?.fileUrl, item?.attachedFileName);
  const [posterFailed, setPosterFailed] = useState(!poster);

  return (
    <>
      {!loaded && <ThumbSpinner accent={accent} />}
      {!posterFailed ? (
        <img
          onClick={onClick}
          className={`image-item image-section__img${loaded ? " image-section__img--loaded" : ""}`}
          src={poster}
          alt=""
          onLoad={onLoaded}
          onError={() => setPosterFailed(true)}
        />
      ) : (
        <VideoFrameThumb src={videoSrc} onClick={onClick} onReady={onLoaded} />
      )}
      <span className="material-symbols-outlined media-video-play">
        play_circle
      </span>
    </>
  );
}

const ImageSection = ({
  media,
  props,
  handleScroll,
  _onCheckBoxPress,
  isSelect,
  onOpenEmailItem,
  apiBaseUrl,
  accentColor,
  downloadActive,
  selectionLocked,
  hideDateHeaders,
  hideEmpty,
  viewerMedia,
}) => {
  const { strings } = React.useContext(LanguageContext);
  const dispatch = useDispatch();
  const [imageLoaded, setImageLoaded] = useState({});
  const [playingVideo, setPlayingVideo] = useState(null);

  const getAllChatImages = () => {
    const allImages = [];
    // `viewerMedia`: the full list when this section renders only part of it (one month).
    (viewerMedia || media)?.forEach((file) => {
      file?.items?.forEach((item) => {
        if (item.messageType === "image" && item.source !== "email") {
          allImages.push(item);
        }
      });
    });
    return allImages;
  };

  const handleImageClick = (clickedItem) => {
    if (clickedItem.source === "email") {
      if (onOpenEmailItem) onOpenEmailItem(clickedItem);
      return;
    }
    if (clickedItem.messageType === "video") {
      setPlayingVideo(clickedItem);
      return;
    }
    const allImages = getAllChatImages();
    const clickedIndex = allImages.findIndex((img) => img.id === clickedItem.id);
    dispatch({ type: SET_VIEWER_IS_INTERNAL, viewerIsInternal: props.isInternal });
    dispatch({
      type: SET_VIEWER_IINITAL_INDEX,
      index: clickedIndex >= 0 ? clickedIndex : 0,
    });
    dispatch({ type: SET_VIEWER_IMAGES, ViewerImages: allImages });
  };

  const getThumbUrl = (item) => {
    if (item.messageType === "video") {
      return buildVideoPosterUrl(false, item?.thumbnailUrl, item?.fileUrl);
    }
    return buildImageUrl(false, item?.fileUrl, item?.attachedFileName);
  };

  const accent =
    accentColor ||
    (props.isInternal ? KJUUP_COLORS.internal : KJUUP_COLORS.base_color);
  const selectionLight = hexToRgba(accent, 0.07);
  const selectionBorder = hexToRgba(accent, 0.45);

  const hasAnyImages = media?.some(
    (file) =>
      file?.items?.filter((item) => isVisualMedia(item)).length > 0
  );

  if (hideEmpty && !hasAnyImages) return null;

  return (
    <>
    <div
      className="media-section-container image-section image-section__root"
      style={{
        "--kjuup-selection-light": selectionLight,
        "--kjuup-selection-border": selectionBorder,
        "--kjuup-brand": accent,
      }}
    >
      <div className="image-grid">
        {media?.map((file, index) => (
          <React.Fragment key={`${file.createdAt}-${index}`}>
            {!hideDateHeaders &&
            file?.items?.length > 0 &&
            file?.items?.filter((item) => isVisualMedia(item)).length >
              0 ? (
              <div
                className="date-header image-section__date"
              >
                {file.createdAt}
              </div>
            ) : null}
            <div className="image-row">
              {file?.items?.map(
                (item, itemIndex) =>
                  isVisualMedia(item) && (
                    <div
                      key={item.id || itemIndex}
                      className={`image-container${item.isSelect ? " selected" : ""}${
                        isSelect ? " select-mode" : ""
                      }${
                        downloadActive && !item.isSelect && !item.isDownloading
                          ? " media-dimmed"
                          : ""
                      }`}
                      onClick={() => {
                        if (selectionLocked || item.isDownloading) return;
                        if (isSelect) {
                          _onCheckBoxPress(null, itemIndex, index);
                        }
                      }}
                    >
                      <div
                        className={`image-wrapper${
                          item.messageType === "video" ? " is-video" : ""
                        }`}
                      >
                        {item.source === "email" ? (
                          <EmailImageThumb
                            item={item}
                            apiBaseUrl={apiBaseUrl}
                            accent={accent}
                            loaded={!!imageLoaded[item.id]}
                            onLoaded={() =>
                              setImageLoaded((prev) => ({
                                ...prev,
                                [item.id]: true,
                              }))
                            }
                            onClick={() => {
                              if (!isSelect && !downloadActive) handleImageClick(item);
                            }}
                          />
                        ) : item.messageType === "video" ? (
                          <MediaVideoThumb
                            item={item}
                            accent={accent}
                            loaded={!!imageLoaded[item.id]}
                            onLoaded={() =>
                              setImageLoaded((prev) => ({
                                ...prev,
                                [item.id]: true,
                              }))
                            }
                            onClick={() => {
                              if (!isSelect && !downloadActive) handleImageClick(item);
                            }}
                          />
                        ) : (
                          <>
                            {!imageLoaded[item.id] && (
                              <div className="image-section__loading">
                                <div className="image-skeleton-loader image-section__spinner" />
                              </div>
                            )}
                            <img
                              onClick={() => {
                                if (!isSelect && !downloadActive) handleImageClick(item);
                              }}
                              className={`image-item image-section__img${imageLoaded[item.id] ? " image-section__img--loaded" : ""}`}
                              src={getThumbUrl(item)}
                              alt=""
                              onLoad={() =>
                                setImageLoaded((prev) => ({
                                  ...prev,
                                  [item.id]: true,
                                }))
                              }
                              onError={() =>
                                setImageLoaded((prev) => ({
                                  ...prev,
                                  [item.id]: true,
                                }))
                              }
                            />
                          </>
                        )}
                        {item.source === "email" && (
                          <span
                            title={strings.filter_email || "E-Mail"}
                            className="image-section__email-badge"
                          >
                            E-Mail
                          </span>
                        )}
                        {item.source === "teams" && (
                          <span
                            title="Microsoft Teams"
                            className="image-section__email-badge image-section__teams-badge"
                          >
                            Teams
                          </span>
                        )}
                        {(isSelect || downloadActive) &&
                          (item.isSelect || item.isDownloading || item.downloadDone) && (
                          <div className="selection-overlay">
                            {item.isDownloading ? (
                              <CircularProgressbar
                                value={item.progress || 0}
                                text={`${(item.progress || 0).toString()}%`}
                                className="progress-circle"
                                background
                                styles={buildStyles({
                                  backgroundColor: "#fff",
                                  textColor: accent,
                                  textSize: "16px",
                                  pathColor: accent,
                                })}
                              />
                            ) : item.downloadDone ? (
                              <span
                                className="material-symbols-outlined image-section__done-icon"
                              >
                                check_circle
                              </span>
                            ) : !downloadActive ? (
                              <label
                                className={
                                  props.isInternal
                                    ? "role-label-internal"
                                    : "role-label"
                                }
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={() =>
                                    _onCheckBoxPress(null, itemIndex, index)
                                  }
                                  value={item.isSelect}
                                  type="checkbox"
                                  checked={!!item.isSelect}
                                />
                                <span
                                  className={
                                    props.isInternal
                                      ? "checkmark-label-internal"
                                      : "checkmark-label"
                                  }
                                />
                              </label>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>
                  )
              )}
            </div>
          </React.Fragment>
        ))}
        {!hasAnyImages && (
          <div className="no-media" role="status">
            <span className="media-empty-label">
              {strings.no_media_available || "No files"}
            </span>
          </div>
        )}
      </div>
    </div>
    {playingVideo && (
      <div
        className="media-video-overlay"
        onClick={() => setPlayingVideo(null)}
      >
        <video
          controls
          autoPlay
          playsInline
          poster={buildVideoPosterUrl(false, playingVideo.thumbnailUrl, playingVideo.fileUrl) || undefined}
          onClick={(e) => e.stopPropagation()}
        >
          <source
            src={buildVideoSrc(false, playingVideo.fileUrl, playingVideo.attachedFileName)}
            type={getVideoMimeType(playingVideo.fileUrl)}
          />
        </video>
      </div>
    )}
    </>
  );
};

export default ImageSection;
